import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { showError } from '@/lib/swal'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { fixedAssetService, FixedAsset } from '@/services/fixedAssetService'
import { AssetDisposalListItem } from '@/api/types'

export const AssetDisposalsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [disposals, setDisposals] = useState<AssetDisposalListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  const branchId = user?.selectedBranch?.branchId

  // Load asset disposals - use GetAllAssets API and filter by isSold: true
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadDisposals = async () => {
      try {
        setLoading(true)
        // Get all assets with large page size to get all sold assets
        const response = await fixedAssetService.getAllAssets(
          branchId!,
          1,
          1000 // Large page size to get all assets
        )
        
        // Filter assets where isSold is true
        const soldAssets = (response.data || []).filter((asset: FixedAsset) => {
          return asset.isSold === true
        })
        
        // Map FixedAsset to AssetDisposalListItem
        const mappedDisposals: AssetDisposalListItem[] = soldAssets.map((asset: FixedAsset) => ({
          id: asset.id || 0,
          fixedAssetId: asset.id || 0,
          fixedAssetNumber: asset.fixedAssetNumber || '',
          fixedAssetName: asset.name || '',
          saleDate: asset.sellingDate || '',
          saleAmount: asset.sellingValue || 0,
          fixedAssetAccount: asset.fixedAssetAccountId || 0,
          collectionAccount: 0, // Will be populated from disposal data if available
          gainLossAccount: 0, // Will be populated from disposal data if available
          createdDate: '',
          createdBy: 0,
          modifiedDate: '',
          modifiedBy: 0,
          branchId: asset.branchId || 0,
        }))
        
        // Apply pagination to filtered results
        const startIndex = (pageNumber - 1) * pageSize
        const endIndex = startIndex + pageSize
        const paginatedDisposals = mappedDisposals.slice(startIndex, endIndex)
        
        setDisposals(paginatedDisposals)
        setTotalCount(mappedDisposals.length)
        const calculatedTotalPages = mappedDisposals.length 
          ? Math.ceil(mappedDisposals.length / pageSize) 
          : 0
        setTotalPages(calculatedTotalPages)
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setDisposals([])
        setTotalCount(0)
        setTotalPages(0)
        if (!isNoData) {
          await showError('Error', error.response?.data?.message || t('assetDisposals.messages.failedToLoadDisposals'), 5000)
        }
      } finally {
        setLoading(false)
      }
    }

    loadDisposals()
  }, [branchId, pageNumber, pageSize])

  const filteredDisposals = useMemo(() => {
    if (!searchQuery.trim()) return disposals
    const query = searchQuery.toLowerCase()
    return disposals.filter((item) => {
      return (
        (item.fixedAssetNumber || '').toLowerCase().includes(query) ||
        (item.fixedAssetName || '').toLowerCase().includes(query) ||
        String(item.id || '').includes(query)
      )
    })
  }, [disposals, searchQuery])

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleAddNew = () => {
    navigate('/asset-disposals/new')
  }

  const handleEdit = (item: AssetDisposalListItem) => {
    const itemId = item.id
    if (!itemId) {
      toast.error(t('assetDisposals.messages.invalidDisposalId'))
      return
    }
    navigate(`/asset-disposals/${itemId}/edit`)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      })
    } catch {
      return dateString
    }
  }

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount)
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('assetDisposals.title')}
        description={t('assetDisposals.subtitle')}
        action={{
          label: t('assetDisposals.addNew'),
          onClick: handleAddNew,
          icon: Plus,
        }}
      />

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('assetDisposals.searchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader message={t('assetDisposals.loadingDisposals')} />
          ) : filteredDisposals.length === 0 ? (
            <EmptyState
              title={searchQuery ? t('assetDisposals.noDisposalsFound') : t('assetDisposals.noAssetDisposalsFound')}
              description={
                searchQuery
                  ? t('assetDisposals.tryAdjustingSearch')
                  : t('assetDisposals.getStarted')
              }
              action={{
                label: t('assetDisposals.addNewDisposal'),
                onClick: handleAddNew,
              }}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('assetDisposals.columns.assetNumber')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('assetDisposals.columns.assetName')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('assetDisposals.columns.saleDate')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('assetDisposals.columns.saleAmount')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('assetDisposals.columns.action')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDisposals.map((item) => (
                      <TableRow
                        key={item.id}
                        className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors"
                      >
                        <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                          {item.fixedAssetNumber || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {item.fixedAssetName || 'N/A'}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatDate(item.saleDate)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-sm text-center align-middle">
                          {formatCurrency(item.saleAmount)}
                        </TableCell>
                        <TableCell className="px-4 py-2 text-center align-middle">
                          <div className="flex justify-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(item)}
                              className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">{t('common.pageSize')}:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[70px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">
                      Page {pageNumber} of {totalPages} ({totalCount} total)
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.max(1, p - 1))}
                      disabled={pageNumber === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPageNumber(p => Math.min(totalPages, p + 1))}
                      disabled={pageNumber === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

